/**
 * "Responsible" and "Paid by" for accounts that more than one person pays.
 *
 * Some families take turns: Dad pays one month, Mom the next. The account
 * still has ONE balance. These pieces add two names to the billing table:
 *
 *   Responsible  whose turn it was (filled in from the Payment Schedule when
 *                the month's dues invoice is created; staff can change it)
 *   Paid by      who actually paid
 *
 * Paid by is the ONLY place this screen says who paid, and the server only
 * sends a name when it is a fact: the person recorded as paying, or, for an
 * older autopay payment, the assigned parent when their own card was the one
 * charged. There used to be a second answer here ("charged to", which was
 * really whose turn it was) that this column fell back to; it is gone.
 *
 * Nothing here shows for an account with one person on it and no names on
 * any row, so every other family's billing table looks exactly as it did.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAccountPeople, setInvoiceResponsible } from "../../../api/invoiceAPI";
import { useTheme } from "../../../context/ThemeContext";
import { getInputStyles, getMutedText, getSecondaryText } from "../../../utils/themeUtils";
import { Button } from "../../ui";

export interface AccountPerson {
  id: string;
  label: string;
  role?: string;
  status?: string;
}

/** True for a payment row (it has a payer, not a responsible person). */
export const isPaymentRow = (invoice: any): boolean =>
  invoice?.type === "credit" || invoice?.metadata?.type === "credit" || !!invoice?.metadata?.display_payment_method;

/**
 * The people on an account. Fails quietly to an empty list: the names are an
 * extra, and the billing table must still load without them.
 */
export function useAccountPeople(token: string | null | undefined, membershipId: string | null | undefined) {
  const [people, setPeople] = useState<AccountPerson[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!token || !membershipId) {
      setPeople([]);
      return undefined;
    }
    // Started inside a promise chain so that ANY failure, including one thrown
    // before the request is made, lands in the catch below instead of taking
    // the billing table down with it.
    Promise.resolve()
      .then(() => getAccountPeople(token, membershipId))
      .then((res: any) => {
        if (!cancelled) setPeople(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        // Already reported by the API layer. No names is a safe fallback:
        // the columns simply do not appear.
        if (!cancelled) setPeople([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, membershipId]);

  return people;
}

/**
 * Should the two columns be drawn at all?
 * Yes when two or more people are on the account, or when any row already
 * carries a name (so history stays readable after someone is removed).
 */
export function showPeopleColumns(people: AccountPerson[], invoices: any[] | null | undefined): boolean {
  if ((people || []).length >= 2) return true;
  return (invoices || []).some((inv) => inv?.responsible_label || inv?.paid_by_label);
}

/** Who paid, for a payment row. */
export function paidByLabel(invoice: any): string | null {
  return invoice?.paid_by_label || null;
}

/**
 * Who the payment form should start on: the person responsible for the
 * newest invoice that is still unpaid and names someone. Empty when nothing
 * names anyone, so the form never guesses.
 */
export function defaultPaidById(invoices: any[] | null | undefined, people: AccountPerson[]): string {
  const ids = new Set((people || []).map((p) => p.id));
  const open = (invoices || [])
    .filter(
      (inv) =>
        !isPaymentRow(inv) &&
        inv?.responsible_membership_user_id &&
        ids.has(inv.responsible_membership_user_id) &&
        inv?.status !== "paid" &&
        inv?.status !== "cancelled" &&
        inv?.post_status !== "draft"
    )
    .sort((a, b) => String(b?.due_date || "").localeCompare(String(a?.due_date || "")));
  return open[0]?.responsible_membership_user_id || "";
}

interface ResponsibleCellProps {
  invoice: any;
  people: AccountPerson[];
  /** Staff on the membership page may change the name. */
  canEdit: boolean;
  token: string | null | undefined;
  /** Called after a successful save so the table can reload. */
  onSaved: () => void;
}

/**
 * The Responsible cell for one row. A payment row shows a dash. Staff get a
 * dropdown; everyone else reads the name.
 */
export const ResponsibleCell: React.FC<ResponsibleCellProps> = ({ invoice, people, canEdit, token, onSaved }) => {
  const { isDarkMode } = useTheme();
  const [saving, setSaving] = useState(false);
  const current: string = invoice?.responsible_membership_user_id || "";

  const save = useCallback(
    async (next: string) => {
      if (!token || next === current) return;
      setSaving(true);
      try {
        const res: any = await setInvoiceResponsible(token, invoice.id, next || null);
        if (res?.success) {
          toast.success(next ? "Responsible person saved." : "Set back to the account.");
          onSaved();
        } else {
          toast.error(res?.error || "Could not save the responsible person.");
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.error || "Could not save the responsible person.");
      } finally {
        setSaving(false);
      }
    },
    [token, current, invoice?.id, onSaved]
  );

  if (isPaymentRow(invoice)) return <span aria-hidden="true">—</span>;

  if (!canEdit || people.length < 2) {
    return <span>{invoice?.responsible_label || "Account"}</span>;
  }

  // The current name may belong to someone since removed from the account;
  // keep it selectable so the dropdown never shows the wrong person.
  const known = people.some((p) => p.id === current);
  return (
    <select
      aria-label="Responsible"
      value={current}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => save(e.target.value)}
      className={`px-2 py-1 rounded text-sm max-w-[11rem] ${getInputStyles(isDarkMode)} disabled:opacity-60`}
    >
      <option value="">Account</option>
      {!known && current && <option value={current}>{invoice?.responsible_label || "Former user"}</option>}
      {people.map((p) => (
        <option key={p.id} value={p.id}>
          {p.label}
        </option>
      ))}
    </select>
  );
};

/** The Paid by cell for one row. Only a payment row has a payer. */
export const PaidByCell: React.FC<{ invoice: any }> = ({ invoice }) => {
  if (!isPaymentRow(invoice)) return <span aria-hidden="true">—</span>;
  return <span>{paidByLabel(invoice) || "—"}</span>;
};

interface SharedAccountNoteProps {
  /** Staff see where the schedule lives and that the name is a label. */
  forStaff: boolean;
  /** Opens Payment Settings, where the Payment Schedule is. */
  onOpenPaymentSchedule?: () => void;
}

/**
 * Says plainly what the two names do NOT mean. Shown in the row's detail
 * pop-up, under Responsible. A parent reading
 * "Responsible: Dad" must not conclude they owe nothing.
 */
export const SharedAccountNote: React.FC<SharedAccountNoteProps> = ({ forStaff, onOpenPaymentSchedule }) => {
  const { isDarkMode } = useTheme();
  return (
    <div
      role="note"
      data-testid="shared-account-note"
      className={`rounded-md border px-3 py-2 text-sm ${
        isDarkMode ? "border-gray-700 bg-gray-800/40" : "border-gray-200 bg-gray-50"
      } ${getSecondaryText(isDarkMode)}`}
    >
      <p>
        <strong>This account has one balance.</strong> Responsible shows whose turn it was and Paid by shows who
        paid. Neither one splits what is owed: late notices and holds apply to the whole account, whoever is named.
      </p>
      {forStaff && (
        <p className={`mt-1 text-xs ${getMutedText(isDarkMode)}`}>
          Monthly dues are filled in from the family&apos;s Payment Schedule when the invoice is created. Changing a
          name here is a label only. It does not change the amount or which card autopay charges.{" "}
          {onOpenPaymentSchedule && (
            <Button type="button" variant="ghost" size="xs" onClick={onOpenPaymentSchedule}>
              Open Payment Schedule
            </Button>
          )}
        </p>
      )}
    </div>
  );
};
